// src/pages/FarmerScanPage.tsx — PDF Screen 3 + 7 (public, no login)
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getScanBag, submitFarmerScan, sendOtp, getFarmerStatus } from '../services/api';
import { css } from '../utils/css';
import { apiStatus, apiErrorCode } from '../utils/errors';

// GET /api/scan/:bagId → data. Unregistered bags return the base fields; a
// DELIVERED bag also carries the farmer_* / delivered_at registration details.
interface ScanBagData {
  bag_id?: string;
  gtin?: string | null;
  batch?: string | null;
  weight_kg?: number | null;
  mfg_date?: string | null;
  status?: string;
  company?: string | null;
  dealer?: string | null;
  district?: string | null;
  state?: string | null;
  subsidy_amt?: number | string | null;
  farmer_name?: string | null;
  farmer_village?: string | null;
  farmer_district?: string | null;
  farmer_location?: string | null;
  delivered_at?: string | null;
}

// GET /api/scan/farmer-status — NOTE: the backend returns these fields at the
// TOP level of the body (not under `data`), and the page stores the whole body.
interface FarmerStatus {
  success: boolean;
  is_first: boolean;
  used: number;
  quota: number | null;
  remaining: number | null;
  max_boori: number;
}

interface GpsFix { lat: string; lng: string; accuracy: number }

// Reverse-geocoded address (backend /api/scan/geocode, Photon or BigDataCloud).
interface GeoAddress {
  place: string;
  district: string;
  state: string;
  full?: string;
  pincode?: string;
}

type GpsStatus = 'idle' | 'detecting' | 'found' | 'denied' | 'unavailable' | 'toofar';
type Step = 'loading' | 'form' | 'success' | 'error' | 'already' | 'notfound' | 'flagged';

interface ScanForm {
  farmer_name: string; mobile: string; village: string; district: string;
  location_name: string; aadhaar: string; otp: string; num_boori: string;
}

// Third-party reverse-geocoder response shapes (only the fields read here).
interface GeocodeApiResponse { success?: boolean; data?: GeoAddress }
interface PhotonProperties {
  street?: string; name?: string; locality?: string; neighbourhood?: string; suburb?: string;
  county?: string; city?: string; district?: string; state?: string;
}
interface PhotonResponse { features?: { properties?: PhotonProperties }[] }
interface BigDataCloudResponse {
  locality?: string; city?: string; principalSubdivision?: string;
  localityInfo?: { informative?: unknown };
}
interface BdcInformative { name?: string; description?: string }

// Verhoeff checksum — validates a 12-digit Aadhaar is structurally valid (not random).
const _vd = [[0,1,2,3,4,5,6,7,8,9],[1,2,3,4,0,6,7,8,9,5],[2,3,4,0,1,7,8,9,5,6],[3,4,0,1,2,8,9,5,6,7],[4,0,1,2,3,9,5,6,7,8],[5,9,8,7,6,0,4,3,2,1],[6,5,9,8,7,1,0,4,3,2],[7,6,5,9,8,2,1,0,4,3],[8,7,6,5,9,3,2,1,0,4],[9,8,7,6,5,4,3,2,1,0]];
const _vp = [[0,1,2,3,4,5,6,7,8,9],[1,5,7,6,2,8,3,0,9,4],[5,8,0,3,7,9,6,1,4,2],[8,9,1,6,0,4,3,5,2,7],[9,4,5,3,1,2,6,8,7,0],[4,2,8,6,5,7,3,9,0,1],[2,7,9,3,8,0,6,4,1,5],[7,0,4,6,9,1,3,2,5,8]];
function isAadhaarValid(num: string): boolean {
  const s = String(num || '').replace(/\D/g, '');
  if (s.length !== 12 || s[0] === '0' || s[0] === '1') return false;
  let c = 0;
  s.split('').reverse().forEach((ch, i) => { c = _vd[c][_vp[i % 8][parseInt(ch, 10)]]; });
  return c === 0;
}

// The API's `{ error }` message, exactly as sent (no fallbacks) — apiError()
// would also fall back to the axios message, which this page must not show.
function apiServerError(e: unknown): string | undefined {
  const ax = e as { response?: { data?: { error?: unknown } } } | null;
  const msg = ax?.response?.data?.error;
  return typeof msg === 'string' && msg ? msg : undefined;
}

/** Escape text before writing it into the receipt window's HTML. */
function escapeHtml(v: string | number): string {
  return String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

export default function FarmerScanPage() {
  const { bagId = '' } = useParams<'bagId'>();
  const [bag,       setBag]       = useState<ScanBagData | null>(null);
  const [form,      setForm]      = useState<ScanForm>({ farmer_name:'', mobile:'', village:'', district:'', location_name:'', aadhaar:'', otp:'', num_boori:'' });
  const [fstatus,   setFstatus]   = useState<FarmerStatus | null>(null);   // { is_first, quota, used, remaining, max_boori } for the entered mobile
  const [gps,       setGps]       = useState<GpsFix | null>(null);
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>('idle'); // idle|detecting|found|denied|unavailable
  const [address,   setAddress]   = useState<GeoAddress | null>(null);        // reverse-geocoded {village, district, state}
  const [step,    setStep]    = useState<Step>('loading'); // loading|form|success|error|already|notfound
  const [loading, setLoading] = useState(false);
  const [otpSent,    setOtpSent]    = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [devOtp,     setDevOtp]     = useState('');   // shown only while SMS is in mock mode (no MSG91 keys)
  const [error,      setError]      = useState('');
  const [queued,     setQueued]     = useState(false);   // this submission was saved offline, not sent
  const [queuedCount, setQueuedCount] = useState<number>(() => {
    try { return (JSON.parse(localStorage.getItem('scan_queue') || '[]') as unknown[]).length; } catch { return 0; }
  });

  useEffect(() => {
    // Get bag details
    getScanBag<ScanBagData>(bagId)
      .then(r => {
        const d = r.data.data;
        setBag(d);
        if (r.data.already_registered || d.status === 'DELIVERED') {
          setStep('already');     // show the registered details
        } else {
          setStep('form');
        }
      })
      .catch((err) => {
        if (apiStatus(err) === 404) {
          setStep('notfound');    // real "bag not found"
        } else if (apiErrorCode(err) === 'BAG_FLAGGED') {
          setStep('flagged');     // fraud-flagged bag — must never get a registration form
        } else if (process.env.NODE_ENV === 'development') {
          // Local dev only: demo bag so the form can be tried without a backend.
          setBag({ bag_id: bagId, batch:'B2024-06', weight_kg:50, district:'MH-NAS', status:'Undelivered', company:'IFFCO' });
          setStep('form');
        } else {
          setStep('error');       // server/network problem — offer a retry, never fake data
        }
      });

    // Watch the geolocation permission. This is what recovers from an accidental
    // "Block": the moment the farmer flips it to Allow in browser settings, this
    // fires and we capture the precise GPS automatically — NO page reload needed.
    let permObj: PermissionStatus | undefined;
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(p => {
          permObj = p;
          // Ask for location the MOMENT the farmer opens the scan page:
          //  • granted → capture straight away
          //  • prompt  → fire the browser's "Allow location?" dialog now
          //  • denied  → show the "Turn ON Location" guide immediately
          if (p.state === 'denied') setGpsStatus('denied');
          else                      captureLocation();
          p.onchange = () => {
            if (p.state === 'denied') setGpsStatus('denied');
            else                      captureLocation();
          };
        })
        .catch(() => { captureLocation(); });   // Permissions API blocked → just ask
    } else {
      captureLocation();   // no Permissions API → ask for location right on scan
    }

    // Offline queue — process any queued submissions
    processOfflineQueue();

    return () => { if (permObj) permObj.onchange = null; };
  }, [bagId]);

  // While BLOCKED, keep checking in the background. The instant the farmer
  // switches Location to "Allow" in settings, the form refreshes itself and
  // captures precise GPS — no manual reload needed. (Backup for browsers where
  // the permission 'onchange' event doesn't fire reliably.)
  useEffect(() => {
    if (gpsStatus !== 'denied') return;
    const t = setInterval(() => {
      if (navigator.permissions?.query) {
        navigator.permissions.query({ name: 'geolocation' })
          .then(p => { if (p.state === 'granted') captureLocation(); })
          .catch(() => {});
      }
    }, 1500);
    return () => clearInterval(t);
  }, [gpsStatus]);

  // Capture real GPS, then reverse-geocode it into village / district / state.
  // Uses watchPosition to REFINE the fix: the first reading is often coarse
  // (network-based); GPS accuracy improves over a few seconds as it locks on.
  // We keep the most accurate reading and stop early once it's tight (≤20m).
  const captureLocation = () => {
    if (!navigator.geolocation) { setGpsStatus('unavailable'); return; }
    setGpsStatus('detecting');
    setGps(null);

    let best: GeolocationPosition | null = null, settled = false, watchId: number | null = null;
    const ACCEPT = 200;   // max acceptable accuracy (m) — coarser than this = reject & retry
    const TIGHT  = 50;    // good-enough fix → stop refining early

    const finish = async () => {
      if (settled) return;
      settled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      // No fix at all → the farmer's Location/GPS is off (or blocked) → show the
      // "Turn ON Location" guide. A fix but coarser than 200m (laptop/WiFi/indoor)
      // → ask them to move to open sky instead of saving a km-off location.
      if (!best) { setGpsStatus('denied'); return; }
      if (best.coords.accuracy > ACCEPT) { setGpsStatus('toofar'); return; }
      const lat = best.coords.latitude.toFixed(6);
      const lng = best.coords.longitude.toFixed(6);
      setGps({ lat, lng, accuracy: Math.round(best.coords.accuracy) });
      setGpsStatus('found');
      const detected = await reverseGeocode(lat, lng);
      if (detected) {
        setAddress(detected);
        // Auto-fill ONLY Location Name (the accurate place) from GPS.
        // District is MANUAL — farmer fills it. Village is also manual.
        setForm(f => ({ ...f, location_name: f.location_name || detected.place || '' }));
      }
    };

    watchId = navigator.geolocation.watchPosition(
      (pos: GeolocationPosition) => {
        if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
        // live feedback so the farmer sees the accuracy tightening
        const cur: GeolocationPosition = best;
        setGps({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6), accuracy: Math.round(cur.coords.accuracy) });
        if (cur.coords.accuracy <= TIGHT) finish();   // accurate enough → done
      },
      (err: GeolocationPositionError) => {
        // code 1 = PERMISSION_DENIED (farmer tapped Block) → recovery guide.
        // code 2/3 = transient — keep waiting; the 12s timeout below finalises.
        if (err.code === 1) { settled = true; if (watchId != null) navigator.geolocation.clearWatch(watchId); setGpsStatus('denied'); }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );

    // Refine for up to 25s to get a precise lock, then finalise
    // (accept ≤200m, otherwise show the "move to open sky" retry).
    setTimeout(finish, 25000);
  };

  // Reverse-geocode coords → {place (EXACT), district, state}.
  // Order matters: our backend (Nominatim zoom=18) gives the true locality/village;
  // Photon often returns only the TEHSIL, so it is a fallback and its tehsil-level
  // names are rejected. A name that is only an admin area is never used as "place".
  const reverseGeocode = async (lat: string, lng: string): Promise<GeoAddress | null> => {
    // Trailing admin words ("… Tehsil") are stripped; a name that is ONLY an admin
    // area (tehsil/district/mandal/taluk) is rejected so we keep looking for the
    // real locality.
    const ADMIN = /\b(tehsil|tahsil|taluk|taluka|taluqa|mandal|sub[- ]?division|subdivision|district|circle|block)\b/i;
    const strip = (s: string | undefined): string => (s || '').replace(/[\s,]*(tehsil|tahsil|taluk|taluka|taluqa|mandal|sub[- ]?division|subdivision|district|circle|block)\s*$/i, '').trim();
    const ok    = (s: string | undefined): s is string => !!s && !ADMIN.test(s);

    // 1) Our backend → Nominatim zoom=18 (road + neighbourhood / village) — most exact
    try {
      const r = await fetch(`/api/scan/geocode?lat=${lat}&lng=${lng}`);
      if (r.ok) {
        const j: GeocodeApiResponse | null = await r.json();
        if (j?.success && j.data && ok(j.data.place)) return j.data;
      }
    } catch { /* fall through */ }

    // 2) Photon (komoot) — client-side; prefer the FINEST fields, skip tehsil-level names
    try {
      const _c = new AbortController(); const _t = setTimeout(() => _c.abort(), 3000);
      const r = await fetch(`https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}&lang=en`, { signal: _c.signal }).finally(() => clearTimeout(_t));
      if (r.ok) {
        const j: PhotonResponse | null = await r.json();
        const p = j?.features?.[0]?.properties;
        if (p) {
          const fine = [p.street, p.name, p.locality, p.neighbourhood, p.suburb].map(strip).filter(ok);
          const place = [...new Set(fine)].slice(0, 2).join(', ');
          if (place) return {
            place,
            district: strip(p.county || p.city || p.district || ''),
            state:    p.state || '',
          };
        }
      }
    } catch { /* fall through */ }

    // 3) BigDataCloud fallback (client-side)
    try {
      const r = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
      );
      const j: BigDataCloudResponse = await r.json();
      const informative = j.localityInfo?.informative;
      const info: BdcInformative[] = Array.isArray(informative) ? informative : [];
      const fine = info.find((x: BdcInformative) => /(neighbourhood|suburb|village|hamlet|town|quarter|locality|ward)/i.test(x.description || ''))?.name;
      const place = strip(j.locality || fine || j.city || '');
      return { place, district: j.city || '', state: j.principalSubdivision || '' };
    } catch { return null; }
  };

  const processOfflineQueue = async () => {
    const queue = JSON.parse(localStorage.getItem('scan_queue') || '[]') as Record<string, unknown>[];
    if (!queue.length || !navigator.onLine) return;
    // Keep only items that failed for a temporary reason (no response / server
    // error) so they are retried later. Items the server accepted, or rejected
    // for good (4xx: expired OTP, already registered…), leave the queue.
    const retry: Record<string, unknown>[] = [];
    for (const item of queue) {
      try {
        await submitFarmerScan(item);
      } catch (err) {
        const st = apiStatus(err);
        if (st === undefined || st >= 500) retry.push(item);
      }
    }
    if (retry.length) localStorage.setItem('scan_queue', JSON.stringify(retry));
    else localStorage.removeItem('scan_queue');
    setQueuedCount(retry.length);
  };

  // Location is MANDATORY — farmer must allow GPS before the form can be submitted
  const locationReady = gpsStatus === 'found' && !!gps;

  // Step 1 — validate details + GPS, then send OTP to the entered mobile
  const sendOtpNow = async () => {
    setError('');
    if (!locationReady) { setError('Please allow location access first.'); return; }
    if (!form.farmer_name.trim() || !form.village.trim()) { setError('Please enter your Name and Village.'); return; }
    if (!/^[6-9]\d{9}$/.test(form.mobile))    { setError('Please enter a valid 10-digit mobile number.'); return; }
    if (!isAadhaarValid(form.aadhaar))  { setError('Please enter a valid 12-digit Aadhaar number.'); return; }
    const maxB = fstatus?.max_boori || 10;
    if (fstatus?.is_first === false) {
      // Later scan of the day → District required, quota must have room
      if (!form.district.trim()) { setError('Please enter your District.'); return; }
      if (fstatus.remaining === 0) { setError(`You have already registered your ${fstatus.quota} bags — no more allowed today.`); return; }
    } else {
      // First scan → declare Number of Bags
      const n = parseInt(form.num_boori);
      if (!n || n < 1 || n > maxB) { setError(`How many bags are you taking? Please enter a number between 1 and ${maxB}.`); return; }
    }
    setOtpSending(true);
    try {
      const r = await sendOtp(form.mobile, bagId);
      setOtpSent(true);
      const devCode = r?.data?._dev_otp; if (devCode) setDevOtp(String(devCode)); // shown only in mock mode (no MSG91 keys)
    } catch (err) {
      if (apiStatus(err) === 429) setError('Too many OTP requests. Please try again after 10 minutes.');
      else if (apiStatus(err) === 404) setError('Bag record not found.');
      else setError('There was a problem sending the OTP. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  // Step 2 — verify OTP + Aadhaar and submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!locationReady) return;   // hard block — no GPS, no submit
    if (!form.farmer_name || !form.mobile || !form.village) return;
    if (!otpSent)                  { setError('Please send the OTP first.'); return; }
    if (!/^\d{6}$/.test(form.otp)) { setError('Please enter the 6-digit OTP.'); return; }
    setLoading(true);

    const payload: Record<string, unknown> = {
      bag_id:        bagId,
      farmer_name:   form.farmer_name,
      mobile:        form.mobile,
      village:       form.village,
      district:      form.district,                            // manual (2nd+ scan)
      num_boori:     form.num_boori || undefined,              // 1st scan only — declares quota
      location_name: form.location_name || address?.place || '', // accurate place from GPS
      aadhaar:       form.aadhaar,
      otp:           form.otp,
      state:        address?.state || bag?.state || '',   // auto-detected from GPS
      latitude:     gps?.lat,
      longitude:    gps?.lng,
    };

    try {
      await submitFarmerScan(payload);
      setStep('success');
    } catch (err) {
      const code = apiErrorCode(err);
      const serverMsg = apiServerError(err);
      if (code === 'PROCESSING') {
        // Another request holds the lock for this bag — a retry is enough.
        setError('This bag is being processed. Please wait a few seconds and submit again.');
      } else if (code === 'BAG_FLAGGED') {
        setStep('flagged');
      } else if (code === 'ALREADY_DELIVERED' || (apiStatus(err) === 409 && !code)) {
        // Already registered — reload the details to show on the 'already' screen
        try { const r = await getScanBag<ScanBagData>(bagId); setBag(r.data.data); } catch {}
        setStep('already');
      } else if (code === 'OTP_INVALID') {
        setError('The OTP is incorrect or has expired. Please request a new OTP.');
        setOtpSent(false); setDevOtp(''); setForm(f => ({ ...f, otp:'' }));
      } else if (code === 'FRAUD_BLOCKED') {
        setError('Suspicious activity detected. Helpline: 1800-180-1551');
      } else if (serverMsg) {
        setError(serverMsg);
      } else if (!navigator.onLine) {
        // Offline — queue it
        const queue = JSON.parse(localStorage.getItem('scan_queue') || '[]') as Record<string, unknown>[];
        queue.push(payload);
        localStorage.setItem('scan_queue', JSON.stringify(queue));
        setQueuedCount(queue.length);
        setQueued(true);
        setStep('success'); // success screen shows the "saved offline" variant
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Open a clean, printable delivery receipt in a new tab (Print / Save-as-PDF).
  const viewReceipt = () => {
    const now = new Date();
    const row = (label: string, val: string | number | null | undefined): string => val ? `<tr><td style="padding:6px 0;color:#555">${escapeHtml(label)}</td><td style="padding:6px 0;text-align:right;font-weight:600;color:#111">${escapeHtml(val)}</td></tr>` : '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Delivery Receipt ${escapeHtml(bagId)}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;background:#f4f7f5;margin:0;padding:20px;color:#111}
      .card{max-width:420px;margin:0 auto;background:#fff;border:1px solid #dfe7e2;border-radius:14px;overflow:hidden}
      .head{background:#0f5132;color:#fff;padding:18px 20px;text-align:center}.head h1{margin:0;font-size:18px}.head p{margin:4px 0 0;font-size:12px;opacity:.85}
      .ok{background:#e7f5ec;color:#0f5132;text-align:center;padding:10px;font-weight:600;font-size:13px}
      table{width:100%;border-collapse:collapse;font-size:13px}.wrap{padding:8px 20px 16px}
      .foot{text-align:center;font-size:11px;color:#888;padding:14px 20px;border-top:1px dashed #ddd}
      .btns{max-width:420px;margin:14px auto;display:flex;gap:10px}.btns button{flex:1;padding:12px;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer}
      .p{background:#0f5132;color:#fff}.c{background:#eee;color:#333}@media print{.btns{display:none}}</style></head><body>
      <div class="card">
        <div class="head"><h1>Fertilizer Tracking — Delivery Receipt</h1><p>Urea Bag Registration</p></div>
        <div class="ok">&#10003; Bag Successfully Registered</div>
        <div class="wrap"><table>
          ${row('Bag ID', bagId)}
          ${row('Farmer', form.farmer_name)}
          ${row('Mobile', form.mobile ? '+91 ' + form.mobile : '')}
          ${row('Village', form.village)}
          ${row('District', form.district)}
          ${row('Location', form.location_name)}
          ${row('GPS', gps ? `${gps.lat}°N, ${gps.lng}°E` : '')}
          ${row('Batch', bag?.batch)}
          ${row('Weight', bag?.weight_kg ? bag.weight_kg + ' kg' : '')}
          ${row('Registered on', now.toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}))}
          ${row('Helpline', '1800-180-1551')}
        </table></div>
        <div class="foot">Computer-generated receipt &middot; Fertilizer Tracking</div>
      </div>
      <div class="btns"><button class="p" onclick="window.print()">&#128424; Print / Save PDF</button><button class="c" onclick="window.close()">Close</button></div>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { alert('The popup was blocked — please allow popups in your browser.'); return; }
    w.document.write(html);
    w.document.close();
  };

  // ── Success screen (PDF Screen 7) ──
  if (step === 'success') return (
    <div style={M.page}>
      <div style={M.successCard}>
        <div style={M.successIcon}>✓</div>
        <h2 style={{ fontSize:22, fontWeight:700, color:'var(--g2)', marginBottom:8 }}>{queued ? 'Saved — Will Submit When Online' : 'Bag Successfully Registered!'}</h2>
        <div style={{ fontSize:13, color:'var(--ink3)', lineHeight:1.7 }}>
          <div style={{ fontFamily:'DM Mono,monospace', fontSize:14, color:'var(--g1)', marginBottom:8 }}>Bag ID: {bagId}</div>
          <div>Farmer: <strong style={{ color:'var(--ink)' }}>{form.farmer_name}</strong></div>
          <div>Time: <strong style={{ color:'var(--ink)' }}>{new Date().toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</strong></div>
          {gps && <div>GPS: <strong style={{ color:'var(--ink)' }}>{gps.lat}°N, {gps.lng}°E</strong></div>}
        </div>
        <div style={{ marginTop:14, padding:'10px 14px', background:'var(--gp)', borderRadius:10, fontSize:12, color:'var(--g2)', border:'1px solid var(--g4)' }}>
          {queued
            ? '📶 No internet right now. This registration is saved on this phone and will be sent automatically once you are online. If the OTP expires before then, please scan the bag again.'
            : `✉️ SMS Sent to +91 ${form.mobile.replace(/(\d{5})(\d{5})/,'$1*****')}`}
        </div>
        {/* SMS template (PDF Screen 7 exact) */}
        <div style={{ marginTop:14, padding:'14px', background:'#F8F8F8', borderRadius:10, fontSize:11, color:'var(--ink3)', textAlign:'left', border:'1px solid var(--bor)', fontFamily:'DM Mono,monospace' }}>
          Dear {form.farmer_name},<br/>
          Your Urea bag {bagId} has been registered.<br/>
          Batch: {bag?.batch} | Wt: {bag?.weight_kg}kg<br/>
          Date: {new Date().toLocaleDateString('en-IN')}<br/>
          Helpline: 1800-180-1551
        </div>
        <button onClick={viewReceipt} className="btn btn-secondary" style={{ marginTop:14, width:'100%', justifyContent:'center' }}>
          📄 View My Delivery Receipt
        </button>
      </div>
      <div style={{ textAlign:'center', padding:'12px 20px', background:' var(--g1)', color:'rgba(255,255,255,0.5)', fontSize:11 }}>
        Farmer Helpline: <strong style={{ color:'var(--g4)' }}>1800-180-1551</strong> · Toll Free · 24x7
      </div>
    </div>
  );

  // ── Already registered (with details) ──
  if (step === 'already') return (
    <div style={M.page}>
      <div style={M.card}>
        <div style={{ textAlign:'center', paddingTop:8 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>⚠️</div>
          <h2 style={{ fontSize:18, color:'var(--ambd)', marginBottom:6 }}>Bag Already Registered</h2>
          <p style={{ fontSize:13, color:'var(--ink3)' }}>This bag has already been registered.</p>
        </div>

        {bag && (
          <div style={{ marginTop:16, border:'1px solid var(--bor)', borderRadius:12, overflow:'hidden' }}>
            <div style={{ background:'var(--gpp)', padding:'10px 14px', fontFamily:'DM Mono,monospace', fontSize:13, fontWeight:600, color:'var(--g1)', borderBottom:'1px solid var(--bor)' }}>
              {bag.bag_id || bagId}
            </div>
            <div style={{ padding:'10px 14px' }}>
              {[
                ['Registered to', bag.farmer_name],
                ['Village',       bag.farmer_village],
                ['District',      bag.farmer_district],
                ['Location',      bag.farmer_location],
                ['Registered on', bag.delivered_at ? new Date(bag.delivered_at).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : null],
                ['Weight',        bag.weight_kg ? `${bag.weight_kg} kg` : null],
                ['Batch',         bag.batch],
                ['Company',       bag.company],
                ['District',      bag.district],
              ].filter(([,v]) => v).map(([l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'6px 0', fontSize:12.5, borderBottom:'1px solid var(--gpp)' }}>
                  <span style={{ color:'var(--ink3)' }}>{l}</span>
                  <span style={{ color:'var(--ink)', fontWeight:600, textAlign:'right' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop:14, padding:'10px 14px', background:'var(--ambp)', borderRadius:10, fontSize:12, color:'var(--ambd)', textAlign:'center', lineHeight:1.5 }}>
          If this is not your bag or there is a mistake, please call the helpline
        </div>
        <div style={{ marginTop:10, textAlign:'center', fontSize:15, fontWeight:700, color:'var(--g2)' }}>📞 1800-180-1551</div>
      </div>
    </div>
  );

  // ── Bag flagged for fraud — never offer a registration form ──
  if (step === 'flagged') return (
    <div style={M.page}>
      <div style={M.card}>
        <div style={{ textAlign:'center', padding:'20px 0' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
          <h2 style={{ fontSize:18, color:'var(--red)', marginBottom:8 }}>This Bag Is Under Review</h2>
          <p style={{ fontSize:13, color:'var(--ink3)' }}>This bag has been flagged and cannot be registered right now. Please contact the helpline or your dealer.</p>
          <div style={{ marginTop:16, fontSize:14, fontWeight:700, color:'var(--g2)' }}>📞 1800-180-1551</div>
        </div>
      </div>
    </div>
  );

  // ── Could not load the bag (server / network) — retry, never fake data ──
  if (step === 'error') return (
    <div style={M.page}>
      <div style={M.card}>
        <div style={{ textAlign:'center', padding:'20px 0' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📶</div>
          <h2 style={{ fontSize:18, color:'var(--ink)', marginBottom:8 }}>Could Not Load Bag Details</h2>
          <p style={{ fontSize:13, color:'var(--ink3)' }}>Please check your internet connection and try again.</p>
          <button type="button" className="btn btn-primary" style={{ marginTop:16 }} onClick={() => window.location.reload()}>⟳ Try Again</button>
          <div style={{ marginTop:16, fontSize:14, fontWeight:700, color:'var(--g2)' }}>📞 1800-180-1551</div>
        </div>
      </div>
    </div>
  );

  // ── Bag not found ──
  if (step === 'notfound') return (
    <div style={M.page}>
      <div style={M.card}>
        <div style={{ textAlign:'center', padding:'20px 0' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>❌</div>
          <h2 style={{ fontSize:18, color:'var(--red)', marginBottom:8 }}>Bag Not Found</h2>
          <p style={{ fontSize:13, color:'var(--ink3)' }}>This QR / Bag ID is not in our records. Please scan the QR again or call the helpline.</p>
          <div style={{ marginTop:16, fontSize:14, fontWeight:700, color:'var(--g2)' }}>📞 1800-180-1551</div>
        </div>
      </div>
    </div>
  );

  // ── Loading ──
  if (step === 'loading') return (
    <div style={{ ...M.page, alignItems:'center', justifyContent:'center' }}>
      <div style={{ fontSize:24 }} className="spin">⟳</div>
    </div>
  );

  // ── Main form (PDF Screen 3 exact) ──
  return (
    <div style={M.page}>
      {/* Hero */}
      <div style={M.hero}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.12em', background:'#E8941A', color:'#fff', padding:'3px 10px', borderRadius:20, display:'inline-block', marginBottom:12 }}>FARMER QR PORTAL</div>
        <h1 style={{ fontSize:24, fontWeight:700, color:'#fff', lineHeight:1.2, marginBottom:8 }}>Fertilizer Tracking — Bag Verification</h1>
        {bag && (
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', lineHeight:1.7 }}>
            Bag ID: <strong style={{ fontFamily:'DM Mono,monospace' }}>{bag.bag_id}</strong><br/>
            Batch: {bag.batch} | Weight: {bag.weight_kg}kg<br/>
            {bag.district} District | Status: <span style={{ color:'#FFD580' }}>{bag.status}</span>
          </div>
        )}
      </div>

      {/* Form card */}
      <div style={M.card}>
        <h3 style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>Confirm Your Details</h3>
        <p style={{ fontSize:13, color:'var(--ink3)', marginBottom:20 }}>Please enter accurate information — this is what will be registered in the record</p>

        <form onSubmit={handleSubmit}>
          <fieldset disabled={!locationReady} style={{ border:'none', padding:0, margin:0, opacity: locationReady ? 1 : 0.55 }}>
          <div className="field">
            <label>Full Name <span className="req">*</span></label>
            <input value={form.farmer_name} onChange={e=>setForm(f=>({...f,farmer_name:e.target.value}))} placeholder="Ramesh Kumar" required />
          </div>
          <div className="field">
            <label>Mobile No. <span className="req">*</span></label>
            <input type="tel" value={form.mobile}
              onChange={e=>{ const v=e.target.value.replace(/\D/g,'').slice(0,10); setForm(f=>({...f,mobile:v})); if(otpSent){ setOtpSent(false); setDevOtp(''); setForm(f=>({...f,otp:''})); } if(/^[6-9]\d{9}$/.test(v)){ getFarmerStatus<FarmerStatus>(v).then(r=>setFstatus(r.data)).catch(()=>setFstatus(null)); } else { setFstatus(null); } }}
              placeholder="98765 43210" pattern="[6-9][0-9]{9}" maxLength={10} inputMode="numeric" required />
          </div>
          <div className="field">
            <label>Village <span className="req">*</span></label>
            <input value={form.village} onChange={e=>setForm(f=>({...f,village:e.target.value}))} placeholder="Nashik Rural" required />
          </div>
          {fstatus?.is_first === false ? (
            /* Later scan of the day → District field */
            <div className="field">
              <label>District <span className="req">*</span></label>
              <input value={form.district} onChange={e=>setForm(f=>({...f,district:e.target.value}))} placeholder="Nashik" required />
              <div style={{ fontSize:11, color: fstatus.remaining === 0 ? 'var(--red)' : 'var(--g2)', marginTop:4, fontWeight:600 }}>
                {fstatus.remaining === 0 ? `⚠️ You have already registered your ${fstatus.quota} bags — no more allowed today.` : `Bags ${fstatus.used}/${fstatus.quota} registered — ${fstatus.remaining} remaining`}
              </div>
            </div>
          ) : (
            /* First scan → declare Number of Bags (sets the limit for this phone today) */
            <div className="field">
              <label>Number of Bags <span className="req">*</span></label>
              <input type="tel" inputMode="numeric" value={form.num_boori}
                onChange={e=>setForm(f=>({...f,num_boori:e.target.value.replace(/\D/g,'').slice(0,2)}))}
                placeholder={`1 - ${fstatus?.max_boori || 10}`} maxLength={2} />
              <div style={{ fontSize:11, color:'var(--ink3)', marginTop:4 }}>How many bags are you taking today? (max {fstatus?.max_boori || 10}) — this is set once, and this phone can only scan up to that number.</div>
            </div>
          )}
          <div className="field">
            <label>Location Name <span style={{ fontSize:10, color:'var(--g3)', fontWeight:600 }}>· Auto (exact place from GPS)</span></label>
            <input value={form.location_name} onChange={e=>setForm(f=>({...f,location_name:e.target.value}))} placeholder="The exact place will be filled from GPS" />
          </div>
          <div className="field">
            <label>Aadhaar Number (12 digit) <span className="req">*</span></label>
            <input type="tel" value={form.aadhaar}
              onChange={e=>setForm(f=>({...f,aadhaar:e.target.value.replace(/\D/g,'').slice(0,12)}))}
              placeholder="1234 5678 9012" maxLength={12} inputMode="numeric" required />
            <div style={{ fontSize:11, marginTop:4, color: (form.aadhaar.length===12 && !isAadhaarValid(form.aadhaar)) ? 'var(--red)' : 'var(--ink3)' }}>
              {(form.aadhaar.length===12 && !isAadhaarValid(form.aadhaar))
                ? '⚠️ This Aadhaar number does not look valid — please check it again.'
                : 'Enter the full 12-digit Aadhaar — only the last 4 digits will be stored (safe).'}
            </div>
          </div>
          </fieldset>

          {/* GPS — MANDATORY. Farmer must tap "Enable Location" (reliable prompt). */}
          {gpsStatus === 'found' ? (
            // ✓ Precise GPS captured (status 'found' is only ever set together with a fix, so gps is non-null here)
            <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--gpp)', border:'1.5px solid var(--g4)', borderRadius:10, padding:'10px 14px', marginBottom:16 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--g4)', flexShrink:0, animation:'livePulse 2s infinite' }}></div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--g2)' }}>✓ Location Captured (GPS)</div>
                <div style={{ fontSize:11, fontFamily:'DM Mono,monospace', color:'var(--ink3)' }}>
                  {gps!.lat}°N, {gps!.lng}°E{gps!.accuracy ? ` (±${gps!.accuracy}m)` : ''}
                </div>
                {address && (address.full || address.place || address.district || address.state) && (
                  <div style={{ fontSize:11, color:'var(--g2)', marginTop:3, fontWeight:500 }}>
                    📍 {address.full || [address.place, address.district, address.state, address.pincode]
                          .filter(Boolean)
                          .filter((v, i, arr) => arr.findIndex(x => String(x).toLowerCase().trim() === String(v).toLowerCase().trim()) === i)
                          .join(', ')}
                  </div>
                )}
                {gps!.accuracy > 100 && (
                  <div style={{ fontSize:10.5, color:'var(--ambd)', marginTop:4, lineHeight:1.45 }}>
                    ⚠️ Accuracy ±{gps!.accuracy}m — the location may be slightly off. For an exact fix, scan from a <strong>phone</strong> (GPS on, under open sky).
                    <span onClick={captureLocation} style={{ color:'var(--g3)', cursor:'pointer', fontWeight:600, marginLeft:6 }}>🎯 Retry</span>
                  </div>
                )}
              </div>
            </div>
          ) : gpsStatus === 'detecting' ? (
            // ⟳ Prompt is open / fetching — always show a clear Allow button so the
            // farmer can trigger the browser prompt himself (no phone-settings trip).
            <div style={{ background:'var(--gpp)', border:'1.5px solid var(--g4)', borderRadius:10, padding:'12px 14px', marginBottom:16, textAlign:'center' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:10 }}>
                <span className="spin" style={{ fontSize:16, color:'var(--g2)' }}>⟳</span>
                <div style={{ fontSize:12, color:'var(--g2)', fontWeight:500 }}>
                  {gps?.accuracy ? `Getting your location… currently ±${gps.accuracy}m, improving accuracy…` : 'Finding your location…'}
                </div>
              </div>
              <button type="button" className="btn btn-primary btn-full" onClick={captureLocation}>
                📍 Allow Location
              </button>
              <div style={{ fontSize:11, color:'var(--ink3)', marginTop:6, lineHeight:1.5 }}>
                Tap the button above → the browser will ask <strong>“Allow”</strong> → tap <strong>Allow</strong>.<br/>No need to go into your phone settings.
              </div>
            </div>
          ) : gpsStatus === 'toofar' ? (
            // 🎯 Coarse fix (>200m) — force a retry from a phone with real GPS
            <div style={{ background:'#FDECEC', border:'1.5px solid var(--red)', borderRadius:10, padding:'14px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--red)', marginBottom:6 }}>📍 Could not get an accurate location</div>
              <div style={{ fontSize:11.5, color:'var(--ink3)', marginBottom:12, lineHeight:1.55 }}>
                {gps?.accuracy ? `Currently ±${gps.accuracy}m — a location this far off cannot be used. ` : ''}For an accurate location, scan from a <strong>phone</strong>, under <strong>open sky</strong>, with GPS turned ON. (Laptops/WiFi do not give an accurate location.)
              </div>
              <button type="button" className="btn btn-primary btn-full" onClick={captureLocation}>🎯 Try Again</button>
            </div>
          ) : gpsStatus === 'unavailable' ? (
            // ❌ Device has no GPS
            <div style={{ background:'#FDECEC', border:'1.5px solid var(--red)', borderRadius:10, padding:'14px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--red)', marginBottom:4 }}>📍 Location is REQUIRED</div>
              <div style={{ fontSize:11.5, color:'var(--ink3)', lineHeight:1.55 }}>
                This device does not have GPS. Please scan from a phone that has location.
              </div>
            </div>
          ) : gpsStatus === 'denied' ? (
            // 🔧 Blocked — ONE simple line + Retry. Background-check auto-recovers on Allow.
            <div style={{ background:'#FFF7E6', border:'1.5px solid var(--amb)', borderRadius:10, padding:'14px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--ambd)', marginBottom:6 }}>📍 Turn ON Location</div>
              <div style={{ fontSize:12, color:'var(--ink3)', marginBottom:12, lineHeight:1.6 }}>
                Turn ON your phone's <strong>Location / GPS</strong>, then allow it for this page.<br/>
                (In the address bar: <strong>🔒</strong> → <strong>Location</strong> → <strong>Allow</strong>)
              </div>
              <button type="button" className="btn btn-primary btn-full" onClick={captureLocation}>
                📍 I turned it ON — Retry
              </button>
            </div>
          ) : (
            // 📍 Idle — first ask. Friendly explainer so the farmer taps "Allow" (not Block).
            <div style={{ background:'var(--gpp)', border:'1.5px solid var(--g4)', borderRadius:10, padding:'14px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:13, fontWeight:700, color:'var(--g2)', marginBottom:4 }}>📍 Share your location</div>
              <div style={{ fontSize:11.5, color:'var(--ink3)', marginBottom:12, lineHeight:1.55 }}>
                Location is required to register the bag. When you tap the button below, the browser will ask — please tap <strong style={{ color:'var(--g2)' }}>“Allow”</strong> (not Block).
              </div>
              <button type="button" className="btn btn-primary btn-full" onClick={captureLocation}>
                📍 Turn ON Location
              </button>
            </div>
          )}

          {/* OTP verification — proves the mobile belongs to the farmer.
              Only appears after GPS is captured (location is mandatory). */}
          {!locationReady ? (
            <>
              <button type="button" className="btn btn-primary btn-full" disabled>🔒 WAITING FOR LOCATION…</button>
              <div style={{ marginTop:8, textAlign:'center', fontSize:11, color:'var(--ink3)' }}>
                You can only fill in the form after allowing location access.
              </div>
            </>
          ) : !otpSent ? (
            <button type="button" className="btn btn-primary btn-full" disabled={otpSending} onClick={sendOtpNow}>
              {otpSending ? <span className="spin">⟳</span> : '📩'} SEND OTP
            </button>
          ) : (
            <>
              <div className="field">
                <label>Enter OTP <span className="req">*</span></label>
                <input type="tel" value={form.otp}
                  onChange={e=>setForm(f=>({...f,otp:e.target.value.replace(/\D/g,'').slice(0,6)}))}
                  placeholder="6-digit OTP" maxLength={6} inputMode="numeric" required />
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11, marginTop:4 }}>
                  <span style={{ color:'var(--g2)' }}>✓ OTP sent to {form.mobile}</span>
                  <span style={{ color:'var(--g3)', cursor:'pointer', fontWeight:600 }} onClick={sendOtpNow}>Resend</span>
                </div>
                {devOtp && (
                  <div style={{ marginTop:6, fontSize:11, color:'#8a6d00', background:'#FFF7E0', border:'1px solid #F0D890', borderRadius:6, padding:'4px 8px' }}>
                    DEV/TEST OTP: <strong>{devOtp}</strong> (will not appear once real SMS is enabled)
                  </div>
                )}
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? <span className="spin">⟳</span> : '✓'} SUBMIT & CONFIRM RECEIPT
              </button>
            </>
          )}

          {error && <div className="alert alert-warn" style={{ marginTop:12 }}>{error}</div>}
        </form>

        {/* Offline note */}
        {!navigator.onLine && (
          <div className="alert alert-warn" style={{ marginTop:12 }}>
            {queuedCount > 0
              ? `📶 Offline — ${queuedCount} submission(s) saved on this phone. They will be sent automatically when the internet is back.`
              : '📶 You are offline. Please connect to the internet to register this bag.'}
          </div>
        )}
      </div>

      {/* Features list (PDF Screen 3) */}
      <div style={{ padding:'14px 20px', background:'var(--gpp)', borderTop:'1px solid var(--bor)' }}>
        {[
          ['✓','Bag details auto-loaded from QR scan'],
          ['✓','Simple 3-field form — Name, Mobile, Village'],
          ['✓','GPS auto-captured (no manual entry)'],
          ['✓','Zero app download — browser-native'],
          ['✓','Offline-safe: queues submit on reconnect'],
          ['✓','Instant SMS to farmer after submission'],
        ].map(([icon, text]) => (
          <div key={text} style={{ display:'flex', gap:8, fontSize:12, color:'var(--ink3)', marginBottom:4 }}>
            <span style={{ color:'var(--g3)', fontWeight:700, flexShrink:0 }}>{icon}</span>{text}
          </div>
        ))}
      </div>

      <div style={{ padding:'10px 20px', background:'var(--g1)', textAlign:'center' }}>
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)' }}>QR URL: ureatrak.gov.in/scan/{bagId} · build geo-v4</div>
      </div>

      {/* Helpline */}
      <div style={{ padding:'12px 20px', background:'var(--ink)', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
        <span style={{ fontSize:12, color:'rgba(255,255,255,0.6)' }}>Farmer Helpline:</span>
        <strong style={{ color:'var(--g4)', fontFamily:'DM Mono,monospace', fontSize:14 }}>1800-180-1551</strong>
        <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Toll Free · 24x7</span>
      </div>
    </div>
  );
}

const M = css({
  page:        { minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', maxWidth:480, margin:'0 auto' },
  topGov:      { background:'var(--g1)', padding:'8px 20px', textAlign:'center' },
  hero:        { background:'linear-gradient(135deg, var(--g1), var(--g3))', padding:'28px 20px 36px', position:'relative' },
  card:        { margin:'-20px 16px 0', background:'#fff', borderRadius:16, padding:'20px', boxShadow:'0 8px 32px rgba(0,0,0,0.1)', marginBottom:14, position:'relative', zIndex:1 },
  successCard: { margin:'20px 16px', background:'#fff', borderRadius:16, padding:'24px', boxShadow:'0 8px 32px rgba(0,0,0,0.1)', textAlign:'center' },
  successIcon: { width:64, height:64, borderRadius:'50%', background:'linear-gradient(135deg,var(--g3),var(--g4))', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:28, color:'#fff' },
});
